import React from 'react';
import { motion } from 'framer-motion';
import { Shield, AlertTriangle, Eye, Settings, Globe, Mail, CheckCircle } from 'lucide-react';
import { SUPPORT_PHONE, SUPPORT_PHONE_HREF } from '../config/contact';

const SafetyNoticePage = () => {
  const sections = [
    {
      id: 'intended-purpose',
      title: '1. Intended Purpose',
      icon: <CheckCircle className="w-5 h-5" />,
      content: [
        'Clearway is designed as an assistive mobility aid that can help users become aware of obstacles ahead through sensor-based feedback. The device is intended to complement existing mobility practices and provide additional awareness of objects that may be difficult to detect using a traditional cane alone.',
        'Clearway is not designed to replace a traditional mobility cane, guide dog, or formal orientation and mobility training.',
        'Users should continue to rely on established mobility techniques and exercise caution while navigating their environment.'
      ]
    },
    {
      id: 'detection-limitations',
      title: '2. Detection Limitations',
      icon: <Eye className="w-5 h-5" />,
      content: [
        'Clearway uses electronic sensors to detect objects in front of the user. While the system is designed to improve obstacle awareness, no electronic sensing system can detect all obstacles in all situations.',
        'Detection performance may vary depending on factors including, but not limited to:',
      ],
      list: [
        'Object size, shape, and material',
        'Environmental conditions (rain, fog, lighting, reflections, etc.)',
        'Sensor positioning and user movement',
        'Nearby surfaces or background interference'
      ],
      footer: [
        'Because of these limitations, Clearway cannot guarantee the detection of every obstacle.',
        'Users should remain attentive to their surroundings at all times.'
      ]
    },
    {
      id: 'user-responsibility',
      title: '3. User Responsibility',
      icon: <Shield className="w-5 h-5" />,
      content: [
        'Clearway users are responsible for exercising reasonable judgment and caution while navigating their environment.',
        'The device should always be used in conjunction with safe mobility practices, including the continued use of traditional navigation techniques.',
        'Clearway should not be relied upon as the sole means of obstacle detection.'
      ]
    },
    {
      id: 'development-status',
      title: '4. Product Development Status',
      icon: <Settings className="w-5 h-5" />,
      content: [
        'Clearway originated as a student engineering project and continues to evolve as testing and improvements are made.',
        'Certain features, performance characteristics, or design elements may change as the product is refined through testing and feedback.',
        'Early users may experience updates or modifications as the system develops.'
      ]
    },
    {
      id: 'proper-use',
      title: '5. Proper Use',
      icon: <CheckCircle className="w-5 h-5" />,
      content: ['To help ensure safe operation:'],
      list: [
        'Use the device only for its intended purpose as an assistive mobility aid',
        'Do not modify or tamper with the internal electronics',
        'Avoid exposing the device to extreme environmental conditions unless specified as safe',
        'Inspect the device regularly for damage before use'
      ],
      footer: ['If the device appears damaged or malfunctioning, discontinue use until it can be inspected.']
    },
    {
      id: 'international-use',
      title: '6. International Use and Environment',
      icon: <Globe className="w-5 h-5" />,
      content: [
        'Clearway may be used in different environments and locations, including India and the United Arab Emirates. Environmental conditions and infrastructure may vary between locations, which may affect how the device performs in real-world scenarios.',
        'Users should adapt their mobility practices accordingly.'
      ]
    },
    {
      id: 'limitation-liability',
      title: '7. Limitation of Liability',
      icon: <AlertTriangle className="w-5 h-5" />,
      content: [
        'To the fullest extent permitted by applicable law, Clearway Mobility and its creators shall not be held liable for any indirect, incidental, or consequential damages arising from the use or inability to use the device.',
        'By using Clearway, users acknowledge that assistive technology cannot eliminate all risks associated with navigation in public environments.'
      ]
    },
    {
      id: 'contact',
      title: '8. Contact',
      icon: <Mail className="w-5 h-5" />,
      content: [
        'If you have questions about the safe use of Clearway, please contact:'
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-28 pb-20 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-500/10 border border-yellow-500/20 mb-6">
            <AlertTriangle className="w-4 h-4 text-yellow-400" />
            <span className="text-yellow-400 text-sm font-medium">Important Information</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Safety Notice & Disclaimer</h1>
          <p className="text-gray-400">Last updated: March 4, 2026</p>
        </motion.div>

        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="p-6 rounded-2xl bg-blue-500/5 border border-blue-500/20 mb-8"
        >
          <p className="text-gray-300 leading-relaxed">
            Thank you for your interest in Clearway. This Safety Notice explains important information 
            about the intended use, limitations, and responsibilities associated with using the 
            Clearway smart walking stick.
          </p>
          <p className="text-gray-400 mt-4 font-medium">
            Please read this notice carefully before using the product.
          </p>
        </motion.div>

        {/* Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <motion.section
              key={section.id}
              id={section.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05 }}
              className="p-6 rounded-2xl bg-gradient-to-b from-gray-800/50 to-transparent border border-gray-800/50"
            >
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-400">
                  {section.icon}
                </span>
                {section.title}
              </h2>
              
              <div className="space-y-3 text-gray-400 leading-relaxed pl-13">
                {section.content.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
                
                {section.list && (
                  <ul className="list-disc list-inside space-y-2 ml-4 my-4">
                    {section.list.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                )}
                
                {section.footer && section.footer.map((para, i) => (
                  <p key={i} className="font-medium text-gray-300">{para}</p>
                ))}

                {section.id === 'contact' && (
                  <div className="mt-4 p-4 rounded-xl bg-gray-800/30">
                    <p className="font-semibold text-white mb-2">Clearway Mobility</p>
                    <p>
                      Support:{' '}
                      <a href="mailto:support@clearway-mobility.com" className="text-blue-400 hover:text-blue-300">
                        support@clearway-mobility.com
                      </a>
                    </p>
                    <p>
                      Phone:{' '}
                      <a href={SUPPORT_PHONE_HREF} className="text-blue-400 hover:text-blue-300">
                        {SUPPORT_PHONE}
                      </a>
                    </p>
                  </div>
                )}
              </div>
            </motion.section>
          ))}
        </div>

        {/* Footer Notice */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12 p-6 rounded-2xl bg-yellow-500/5 border border-yellow-500/20 text-center"
        >
          <p className="text-yellow-400/90">
            <strong>Using Clearway indicates that you understand the assistive nature of the device 
            and agree to use it responsibly.</strong>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default SafetyNoticePage;
